import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, apiErr } from "@/lib/api";
import { setUserProperties } from "@/lib/analytics";

const AuthCtx = createContext(null);

// The session cookies are httpOnly, so on a cold load the client cannot tell a
// signed-in visitor from a guest until /auth/me answers. Remember the last
// resolved role so auth-aware routing (see HomeRoute) can act on the first
// paint instead of flashing guest UI. This is a hint, never a credential —
// every protected route still waits for the real /auth/me result.
const HINT_KEY = "auth_role";
const readHint = () => {
  try { return localStorage.getItem(HINT_KEY) || null; } catch { return null; }
};
const writeHint = (role) => {
  try {
    if (role) localStorage.setItem(HINT_KEY, role);
    else localStorage.removeItem(HINT_KEY);
  } catch { /* private mode — routing just falls back to the guest path */ }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=logged in
  const [loading, setLoading] = useState(true);
  // Read once at mount: only consulted while user === null, so it never needs
  // to track later sign-in/sign-out (the resolved user wins from then on).
  const [hintedRole] = useState(readHint);

  // Landing on /auth/verify runs two things against the session at once: the
  // provider's own /auth/me, and verifyLink swapping the cookie underneath it.
  // Whichever response landed last used to win, so a stale /auth/me could
  // reinstate the previous user — showing their name and dashboard to whoever
  // just signed in. Two guards, because a counter alone is not enough:
  //   authGen  — invalidates answers to requests issued before the swap.
  //   swapping — blocks /auth/me from even starting during a swap. AuthVerify is
  //              a child of this provider, so its effect runs first: a refresh
  //              kicked off afterwards would otherwise read the *old* cookie yet
  //              carry the *new* generation, and pass the counter check.
  const authGen = useRef(0);
  const swapping = useRef(false);

  const commit = useCallback((gen, next) => {
    if (gen !== authGen.current) return false; // superseded — drop the stale answer
    setUser(next);
    writeHint(next ? next.role : null);
    if (next) setUserProperties({ user_role: next.role });
    return true;
  }, []);

  // Called by every path that establishes or drops an identity.
  const beginAuthChange = useCallback(() => {
    swapping.current = true;
    return ++authGen.current;
  }, []);
  const endAuthChange = useCallback(() => { swapping.current = false; }, []);

  const refresh = useCallback(async () => {
    // During static prerendering (scripts/prerender.js sets window.__PRERENDER__),
    // leave auth unresolved so the snapshot matches the browser's first render
    // (user === null) — otherwise auth-dependent chrome (Nav) causes a hydration
    // mismatch. The real browser resolves auth normally after hydration.
    if (typeof window !== "undefined" && window.__PRERENDER__) return;
    if (swapping.current) return; // a sign-in/out is authoritative; don't race it
    const gen = authGen.current;
    try {
      const { data } = await api.get("/auth/me");
      // GA4 user property for segmentation (role only — no PII).
      commit(gen, data);
    } catch {
      commit(gen, false); // stale hint (expired session) — stop acting on it
    } finally {
      if (gen === authGen.current) setLoading(false);
    }
  }, [commit]);

  useEffect(() => { refresh(); }, [refresh]);

  // Passwordless: request a one-time email sign-in link.
  const requestLink = async (payload) => {
    const { data } = await api.post("/auth/request-link", payload);
    return data; // { ok, message, dev_link? }
  };

  // Passwordless: exchange a magic-link token for a session.
  const verifyLink = async (token) => {
    const gen = beginAuthChange();
    try {
      const { data } = await api.post("/auth/verify-link", { token });
      commit(gen, data);
      setLoading(false);
      return data;
    } finally {
      endAuthChange();
    }
  };

  // Google Identity Services credential (ID token) → session.
  const googleLogin = async (credential, extra = {}) => {
    const gen = beginAuthChange();
    try {
      const { data } = await api.post("/auth/google", { credential, ...extra });
      commit(gen, data);
      setLoading(false);
      return data;
    } finally {
      endAuthChange();
    }
  };

  const logout = async () => {
    const gen = beginAuthChange();
    try { await api.post("/auth/logout"); } catch (e) {}
    commit(gen, false);
    setLoading(false);
    endAuthChange();
  };

  return (
    <AuthCtx.Provider value={{ user, loading, hintedRole, requestLink, verifyLink, googleLogin, logout, refresh, apiErr }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
